/**
 * AwayDetailsPreview — Read-only away/return details dialog.
 *
 * Follows the Card → Details Preview → Edit Form pattern.
 * Shows return info + Mark Returned button that opens the ArrivalDialog.
 */

import { useState } from 'react'
import { Plane, Clock, MapPin, PlaneLanding } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  formatDate,
  formatRelativeDate,
  getHoursSince,
} from '@/lib/utils/format-business-data'
import { useAppContext } from '@/hooks/routing/useAppContext'
import { ArrivalDialog } from '@/components/ramp-wizard/ArrivalDialog'
import type { AllocationEnrichedData } from '@/types/aircraft-card'

interface AwayDetailsPreviewProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  data: AllocationEnrichedData
}

export function AwayDetailsPreview({ open, onOpenChange, data }: AwayDetailsPreviewProps) {
  const { locationId } = useAppContext()
  const [arrivalDialogOpen, setArrivalDialogOpen] = useState(false)

  const allocation = data.allocation
  const expectedReturn = allocation.expectedReturnAt
  const isOverdue = expectedReturn ? getHoursSince(expectedReturn) > 0 : false
  const locationParts = [allocation.areaName, allocation.unitCode].filter(Boolean)
  const locationName = locationParts.length > 0 ? locationParts.join(' · ') : 'Unassigned'

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plane className="w-4 h-4" />
              Away — {data.aircraft.tailNumber}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            {/* Status */}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Status</span>
              {isOverdue ? (
                <Badge variant="destructive" className="text-xs">Return Overdue</Badge>
              ) : (
                <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50">Away</Badge>
              )}
            </div>

            {/* Assigned space */}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" />Assigned Space
              </span>
              <span>{locationName}</span>
            </div>

            {/* Expected return */}
            {expectedReturn && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />Expected Return
                </span>
                <span className={isOverdue ? 'text-destructive font-medium' : ''}>
                  {formatDate(expectedReturn)}
                  <span className="text-xs ml-1">
                    ({formatRelativeDate(expectedReturn)})
                  </span>
                </span>
              </div>
            )}

            {/* If overdue, show how late */}
            {isOverdue && expectedReturn && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Overdue By</span>
                <span className="text-destructive font-medium">
                  {Math.round(getHoursSince(expectedReturn))}h
                </span>
              </div>
            )}

            {/* No expected return */}
            {!expectedReturn && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />Expected Return
                </span>
                <span className="text-muted-foreground italic">Unknown</span>
              </div>
            )}
          </div>

          {/* Action button */}
          <div className="flex items-center gap-2 pt-2 border-t mt-2">
            <Button
              variant="default"
              size="sm"
              className="h-8 px-4 text-xs"
              onClick={() => {
                onOpenChange(false)
                setArrivalDialogOpen(true)
              }}
            >
              <PlaneLanding className="w-3.5 h-3.5 mr-1.5" />Mark Returned
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Arrival Dialog (edit form) */}
      {locationId && (
        <ArrivalDialog
          open={arrivalDialogOpen}
          onOpenChange={setArrivalDialogOpen}
          tailNumber={data.aircraft.tailNumber}
          allocationId={data.allocationId}
          aircraftId={data.aircraft.id}
          locationId={locationId}
        />
      )}
    </>
  )
}
